/* ------------------------------------------------------------------ *
 * مار پله — THE SCENE'S PICTURE FILES, and warming them before the table
 * needs them (card-86 round 2, batch P; round 3, card-86-r3b). The
 * operator: «the board and game show first … and the picture swaps in
 * once it has arrived» — the preload makes «once it has arrived» usually
 * «already».
 *
 * ROUND 3 — RESOLUTION VARIANTS (the operator, 2026-11-04: «Show each
 * picture sharp, at its real quality … Never stretch a picture beyond its
 * real resolution»). Each picture look ships a ladder of JPEGs cut from
 * its master (`tools/card-art/ladders-scene.cjs`): `scene-<stem>-w<px>.jpg`
 * (+ an optional `.webp` twin each, only where the measured gate passed
 * one) and `scene-<stem>-fill.jpg`, the build-time-blurred fill. THIS
 * module is the ONE place they are found and the ONE rule that picks one
 * (`pickVariant`: the smallest at least as wide as the box's DEVICE px),
 * used by the scene for its measured box and by the preload for the box
 * it estimates — so both ask for the same hashed `/assets/` URL
 * (immutable: the table's request is a cache hit) and each phone
 * downloads only what it needs. `scene-bg*` is «جنگل»'s (its round-1
 * stem), `scene-<style>*` any other picture style's; a style with no
 * files («اورجینال») has no picture.
 *
 * ONE `new Image()` per picture, at most once per page life, when the
 * «مار پله» chooser opens (Lobby.tsx) or a «مار پله» table link lands
 * (`boot/open-route.ts`) — never from the lobby's own render or idle, so
 * the heat gate's `lobby-idle` reads the same. Its format is the one a
 * `<picture>` picks: the WebP when the variant has one AND this browser
 * decodes WebP (a 1×1 lossy decode, asked once), else the JPEG. The fill
 * is under Vite's 4 KB inline limit, so it rides in the bundle: nothing
 * to fetch.
 * ------------------------------------------------------------------ */

const FOUND = import.meta.glob<string>("../../assets/ladders/scene-*.{jpg,webp}", {
  eager: true,
  query: "?url",
  import: "default",
});

/** Each picture's size in its master's px (the tools' measurements) — the preload's estimate. */
const MASTERS = import.meta.glob<{ readonly w: number; readonly h: number }>(
  "../../../tools/card-art/ladders-scene*-measurements.json",
  { eager: true, import: "master" },
);

/** One resolution of a picture: its width in px, its JPEG (always) and its WebP twin (if any). */
export interface SceneVariant {
  readonly w: number;
  readonly jpg: string;
  readonly webp?: string;
}

export interface ScenePictureSources {
  /** Narrowest first; every one has its JPEG. */
  readonly variants: readonly SceneVariant[];
  /** The build-time-blurred fill (`null`: the picture has none). */
  readonly fill: string | null;
}

let found: Readonly<Record<string, string>> = FOUND;

/** `…/scene-<stem>-w<px>.<ext>` or `…/scene-<stem>-fill.<ext>`. */
const NAME = /\/scene-(.+)-(?:w(\d+)|(fill))\.(jpg|webp)$/;
const stemOf = (style: string): string => (style === "jungle" ? "bg" : style);

/** A picture style's files, or `null` when the style has no picture. */
export function sceneSources(style: string): ScenePictureSources | null {
  const stem = stemOf(style);
  const byWidth = new Map<number, { jpg?: string; webp?: string }>();
  let fill: string | null = null;
  for (const [key, url] of Object.entries(found)) {
    const m = NAME.exec(key);
    if (!m || m[1] !== stem) continue;
    if (m[3]) {
      if (m[4] === "jpg") fill = url;
      continue;
    }
    const w = Number(m[2]);
    const entry = byWidth.get(w) ?? {};
    if (m[4] === "jpg") entry.jpg = url;
    else entry.webp = url;
    byWidth.set(w, entry);
  }
  const variants: SceneVariant[] = [];
  for (const [w, e] of [...byWidth.entries()].sort((a, b) => a[0] - b[0])) {
    // A WebP alone is never drawn: the JPEG is the real fallback.
    if (e.jpg) variants.push(e.webp ? { w, jpg: e.jpg, webp: e.webp } : { w, jpg: e.jpg });
  }
  return variants.length > 0 ? { variants, fill } : null;
}

/**
 * The SMALLEST variant at least `needPx` wide — one image px per device px at least — or, when even
 * the widest is narrower (a box the scene caps at that width, see {@link pictureScale}), the widest.
 */
export function pickVariant(
  variants: readonly SceneVariant[],
  needPx: number,
): SceneVariant | null {
  for (const v of variants) if (v.w >= needPx - 1e-6) return v;
  return variants[variants.length - 1] ?? null;
}

/**
 * The picture's scale (CSS px per master px): CONTAINED in `availW`×`availH` — the whole picture,
 * never cropped — and never more device px than `maxW` (its widest variant) has: the picture is
 * never stretched beyond its real resolution, whatever the screen.
 */
export function pictureScale(
  availW: number,
  availH: number,
  master: { readonly w: number; readonly h: number },
  maxW: number,
  dpr: number,
): number {
  const d = dpr > 0 ? dpr : 1;
  return Math.max(0, Math.min(availW / master.w, availH / master.h, maxW / (master.w * d)));
}

/** What the preload knows of the screen the table will draw on (CSS px). */
export interface SceneViewport {
  readonly w: number;
  readonly h: number;
  /** The top inset the picture starts under (`--tg-safe-top`). */
  readonly insetTop: number;
  readonly dpr: number;
}

/** The variant a picture's box needs on `view` — the scene's own rule, on an estimated box. */
export function variantForViewport(
  src: ScenePictureSources,
  master: { readonly w: number; readonly h: number },
  view: SceneViewport,
): SceneVariant | null {
  const widest = src.variants[src.variants.length - 1]?.w ?? 0;
  const s = pictureScale(view.w, view.h - view.insetTop, master, widest, view.dpr);
  return pickVariant(src.variants, master.w * s * view.dpr);
}

/** The screen as the lobby sees it now: the viewport (the table's `--game-h`), its top inset, its DPR. */
function viewportNow(): SceneViewport {
  const root = document.documentElement;
  const css = getComputedStyle(root);
  const px = (name: string): number => {
    const v = parseFloat(css.getPropertyValue(name));
    return Number.isFinite(v) ? v : 0;
  };
  return {
    w: root.clientWidth || window.innerWidth,
    h: px("--game-h") || window.innerHeight,
    insetTop: px("--tg-safe-top"),
    dpr: window.devicePixelRatio || 1,
  };
}

const masterOf = (style: string): { readonly w: number; readonly h: number } | null =>
  MASTERS[
    `../../../tools/card-art/${style === "jungle" ? "ladders-scene" : `ladders-scene-${style}`}-measurements.json`
  ] ?? null;

/** The smallest lossy WebP (1×1) — the format the scene would be served in. */
const WEBP_PROBE =
  "data:image/webp;base64,UklGRiIAAABXRUJQVlA4IBYAAAAwAQCdASoBAAEADsD+JaQAA3AAAAAA";
let webpSupport: Promise<boolean> | null = null;

/** Whether this browser decodes WebP — asked ONCE per page life, then cached. */
export function supportsWebp(): Promise<boolean> {
  if (!webpSupport) {
    webpSupport = new Promise<boolean>((resolve) => {
      if (typeof Image !== "function") {
        resolve(false);
        return;
      }
      const probe = new Image();
      probe.onload = () => resolve(probe.width === 1 && probe.height === 1);
      probe.onerror = () => resolve(false);
      probe.src = WEBP_PROBE;
    });
  }
  return webpSupport;
}

/** The URLs already asked for this page life (each picture once, whichever door asked). */
const warmed = new Set<string>();
/** The WIDEST variant the preload warmed, by picture style — the scene draws it when wide enough. */
const warmedByStyle = new Map<string, SceneVariant>();

/**
 * The variant the preload warmed for `style` this page life, or `null`. Its estimate knows the
 * screen but not the board, so it is never NARROWER than the scene's own pick (the scene may make
 * the picture smaller to keep the landmark's foot above the board) — and the scene draws it then,
 * rather than fetch a second, smaller file.
 */
export function warmedVariant(style: string): SceneVariant | null {
  return warmedByStyle.get(style) ?? null;
}
/** Held until each fetch settles, so nothing collects a preload mid-flight. */
const inFlight = new Set<HTMLImageElement>();

function fetchOnce(url: string): void {
  if (warmed.has(url) || typeof Image !== "function") return;
  warmed.add(url);
  const img = new Image();
  img.decoding = "async";
  const done = (): void => {
    inFlight.delete(img);
  };
  img.onload = done;
  img.onerror = done;
  inFlight.add(img);
  img.src = url;
}

/**
 * Warm `style`'s background (the table's look when it is known; «جنگل» when it is not — a link
 * cannot say which look its table has): the variant this screen's box will pick. Never throws,
 * never repeats a fetch.
 */
export function prefetchLaddersScene(style = "jungle"): void {
  const own = sceneSources(style);
  const pictured = own ? style : "jungle";
  const src = own ?? sceneSources("jungle");
  const master = masterOf(pictured);
  if (!src || !master) return;
  let v: SceneVariant | null = null;
  try {
    v = variantForViewport(src, master, viewportNow());
  } catch {
    v = pickVariant(src.variants, Infinity);
  }
  if (!v) return;
  const before = warmedByStyle.get(pictured);
  if (!before || v.w > before.w) warmedByStyle.set(pictured, v);
  if (!v.webp) {
    fetchOnce(v.jpg);
    return;
  }
  const { jpg, webp } = v;
  void supportsWebp().then((ok) => fetchOnce(ok ? webp : jpg));
}

// ------------------------------------------------------------------------------------ test seams

/** Replace the found files (`{ "<glob key>": url }`) and forget every cached answer. */
export function __setSceneFilesForTests(next: Readonly<Record<string, string>> | null): void {
  found = next ?? FOUND;
  webpSupport = null;
  warmed.clear();
  warmedByStyle.clear();
  inFlight.clear();
}
